import React, {createContext, PropsWithChildren, useContext} from 'react';
import {useFormik} from 'formik';
import dayjs from 'dayjs';
import {useHistory} from 'react-router-dom';

type InspectionsMapFiltersContext = {
  fromDate: string | null,
  updateFromDate: (date: Date | null) => Promise<void>,
  toDate: string | null,
  updateToDate: (date: Date | null) => Promise<void>,
  cityID: number | null,
  updateCityID: (cityID: number | null) => Promise<void>,
  streetID: number | null,
  updateStreetID: (streetID: number | null) => Promise<void>,
}

type FormValues = {
  fromDate: string | null,
  toDate: string | null,
  cityID: number | null,
  streetID: number | null,
}

const InspectionsMapFiltersContext = createContext<InspectionsMapFiltersContext>({
  fromDate: null,
  updateFromDate: async (date: Date | null) => {},
  toDate: null,
  updateToDate: async (date: Date | null) => {},
  cityID: null,
  updateCityID: async (cityID: number | null) => {},
  streetID: null,
  updateStreetID: async (streetID: number | null) => {},
});

export const useInspectionsMapFiltersContext = () => useContext(InspectionsMapFiltersContext);

interface InspectionsMapProviderProps extends PropsWithChildren<any> {}

export const InspectionsMapFiltersProvider = ({children}: InspectionsMapProviderProps) => {
  const history = useHistory();
  const formik = useFormik<FormValues>({
    initialValues: {
      fromDate: null,
      toDate: null,
      cityID: null,
      streetID: null,
    },
    onSubmit: async values => {
      const qs = new URLSearchParams(window.location.search);
      if (values.fromDate) {
        qs.set('fromDate', values.fromDate.toString());
      } else {
        qs.delete('fromDate');
      }
      if (values.toDate) {
        qs.set('toDate', values.toDate.toString());
      } else {
        qs.delete('toDate');
      }
      if (values.cityID) {
        qs.set('cityID', values.cityID.toString());
      } else {
        qs.delete('cityID');
      }
      if (values.streetID) {
        qs.set('streetID', values.streetID.toString());
      } else {
        qs.delete('streetID');
      }

      const path = history.location.pathname;
      history.replace(`${path}?${qs.toString()}`);
    }
  });

  const updateFromDate = async (date: Date | null) => {
    await formik.setFieldValue('fromDate', date ? dayjs(date).format('YYYY-MM-DD') : '');
    formik.handleSubmit();
  };

  const updateToDate = async (date: Date | null) => {
    await formik.setFieldValue('toDate', date ? dayjs(date).format('YYYY-MM-DD') : '');
    formik.handleSubmit();
  };

  const updateCityID = async (cityID: number | null) => {
    await formik.setFieldValue('cityID', cityID);
    await formik.setFieldValue('streetID', null);
    formik.handleSubmit();
  };

  const updateStreetID = async (streetID: number | null) => {
    await formik.setFieldValue('streetID', streetID);
    formik.handleSubmit();
  };

  return (
    <InspectionsMapFiltersContext.Provider value={{
      fromDate: formik.values.fromDate,
      updateFromDate,
      toDate: formik.values.toDate,
      updateToDate,
      cityID: formik.values.cityID,
      updateCityID,
      streetID: formik.values.streetID,
      updateStreetID,
    }}>
      {children}
    </InspectionsMapFiltersContext.Provider>
  );
};
